import { cn } from '../../styles/lib/utils';
import { Button } from '../Button';
import { Icon, type IconName } from '../Icon';
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValue } from '../Select';
import { Text } from '../Text';

export type Density = 'cozy' | 'comfy' | 'roomy';

export const DEFAULT_DENSITY: Density = 'cozy';

/** Which export is currently running, so only that button shows a spinner. */
export type ExportScope = 'all' | 'visible';

/** An extra settings-menu action, such as "Download all files". Shows after the export buttons. */
export type TableMenuAction = {
  label: string;
  icon: IconName;
  onClick: () => void;
  testId: string;
  /** Shows a spinner while the action runs. */
  loading?: boolean;
};

export type TableMenuProps = {
  itemsPerPage: number;
  /** Omit to hide the page-size setting, e.g. for a table without pagination. */
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  density: Density;
  onDensityChange: (density: Density) => void;
  onExportVisible?: () => void;
  onExportAll?: () => void;
  /** Export in progress; the matching button shows a spinner. */
  exporting?: ExportScope;
  actions?: TableMenuAction[];
  onResetView?: () => void;
  canResetView?: boolean;
  className?: string;
};

const perPageOptions = [10, 25, 50, 100];

const densityLabels: Record<Density, string> = {
  cozy: 'Cozy',
  comfy: 'Comfy',
  roomy: 'Roomy',
};

/**
 * Table Settings Menu, optimized for DRF
 */
function TableMenu({
  itemsPerPage,
  onItemsPerPageChange,
  density,
  onDensityChange,
  onExportVisible,
  onExportAll,
  exporting,
  actions,
  onResetView,
  canResetView,
  className,
}: TableMenuProps) {
  return (
    <div className={cn('min-w-[135px] w-max flex flex-col gap-2', className)}>
      {onItemsPerPageChange && (
        <div className="flex items-center justify-between w-full">
          <Text className="text-xs text-primary leading-normal">Per Page</Text>
          <div className="min-w-[68px] h-6">
            <SelectRoot value={String(itemsPerPage)} onValueChange={(value) => onItemsPerPageChange(Number(value))}>
              <SelectTrigger className="h-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="min-w-[68px]">
                {perPageOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between w-full">
        <Text className="text-xs text-primary leading-normal">Density</Text>
        <div className="min-w-[68px] h-6">
          <SelectRoot value={density} onValueChange={(value) => onDensityChange(value as Density)}>
            <SelectTrigger className="h-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="min-w-[68px]">
              {(Object.keys(densityLabels) as Density[]).map((option) => (
                <SelectItem key={option} value={option}>
                  {densityLabels[option]}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
        </div>
      </div>
      {onExportVisible && (
        <Button
          variant="secondary"
          size="small"
          onClick={onExportVisible}
          loading={exporting === 'visible'}
          className="w-full"
          data-testid="table-menu-export-visible"
        >
          <Icon icon="download" size={14} />
          Export Visible
        </Button>
      )}
      {onExportAll && (
        <Button
          variant="secondary"
          size="small"
          onClick={onExportAll}
          loading={exporting === 'all'}
          className="w-full"
          data-testid="table-menu-export-all"
        >
          <Icon icon="download" size={14} />
          Export All
        </Button>
      )}
      {actions?.map((action) => (
        <Button
          key={action.testId}
          variant="secondary"
          size="small"
          onClick={action.onClick}
          loading={action.loading}
          className="w-full"
          data-testid={action.testId}
        >
          <Icon icon={action.icon} size={14} />
          {action.label}
        </Button>
      ))}
      {onResetView && (
        <Button
          variant="danger"
          size="small"
          onClick={onResetView}
          className="w-full"
          disabled={!canResetView}
          data-testid="table-menu-reset-to-default"
        >
          <Icon icon="reset" size={14} />
          Reset to default
        </Button>
      )}
    </div>
  );
}

export { TableMenu };
